import { createContext, useContext, useState, useCallback } from "react";

interface SideMenuState {
  isMobileOpen: boolean;
  openMobileMenu: () => void;
  closeMobileMenu: () => void;
  toggleMobileMenu: () => void;
  isCollapsed: boolean;
  setIsCollapsed: (collapsed: boolean) => void;
  toggleCollapsed: () => void;
}

const SideMenuStateContext = createContext<SideMenuState | undefined>(
  undefined
);

export const SideMenuStateProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [isCollapsed, setIsCollapsedState] = useState(false);

  const openMobileMenu = useCallback(() => {
    setIsMobileOpen(true);
  }, []);

  const closeMobileMenu = useCallback(() => {
    setIsMobileOpen(false);
  }, []);

  const toggleMobileMenu = useCallback(() => {
    setIsMobileOpen((prev) => !prev);
  }, []);

  const setIsCollapsed = useCallback((collapsed: boolean) => {
    setIsCollapsedState(collapsed);
  }, []);

  const toggleCollapsed = useCallback(() => {
    setIsCollapsedState((prev) => !prev);
  }, []);

  return (
    <SideMenuStateContext.Provider
      value={{
        isMobileOpen,
        openMobileMenu,
        closeMobileMenu,
        toggleMobileMenu,
        isCollapsed,
        setIsCollapsed,
        toggleCollapsed,
      }}
    >
      {children}
    </SideMenuStateContext.Provider>
  );
};

export const useSideMenuState = (): SideMenuState => {
  const context = useContext(SideMenuStateContext);
  if (context === undefined) {
    throw new Error(
      "useSideMenuState must be used within a SideMenuStateProvider"
    );
  }
  return context;
};
